(() => {
  if (customElements.get('watch-read-tabs')) return;

  class WatchReadTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.tabs = Array.from(this.querySelectorAll('[data-watch-read-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-watch-read-panel]'));
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);

      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);

      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', this.handleBlockSelect);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);

      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }

      this.initialized = false;
    }

    handleClick(event) {
      const playButton = event.target.closest('[data-watch-read-play]');
      if (playButton && this.contains(playButton)) {
        this.loadVideo(playButton);
        return;
      }

      const tab = event.target.closest('[data-watch-read-tab]');
      if (!tab || !this.contains(tab)) return;

      this.activateTab(tab);
    }

    loadVideo(playButton) {
      const media = playButton.closest('[data-watch-read-video]');
      const videoId = media?.dataset.videoId?.trim();
      if (!media || !videoId || media.dataset.videoLoaded === 'true') return;

      const iframe = document.createElement('iframe');
      const videoUrl = new URL(`https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}`);
      videoUrl.searchParams.set('autoplay', '1');
      videoUrl.searchParams.set('rel', '0');
      videoUrl.searchParams.set('playsinline', '1');

      iframe.className = 'watch-read-tabs__iframe';
      iframe.src = videoUrl.toString();
      iframe.title = media.dataset.videoTitle || 'YouTube video';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';

      media.dataset.videoLoaded = 'true';
      media.replaceChildren(iframe);
    }

    handleKeydown(event) {
      const tab = event.target.closest('[data-watch-read-tab]');
      if (!tab || !this.contains(tab)) return;

      const currentIndex = this.tabs.indexOf(tab);
      let targetIndex = currentIndex;

      switch (event.key) {
        case 'ArrowLeft':
          targetIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
          break;
        case 'ArrowRight':
          targetIndex = (currentIndex + 1) % this.tabs.length;
          break;
        case 'Home':
          targetIndex = 0;
          break;
        case 'End':
          targetIndex = this.tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.activateTab(this.tabs[targetIndex], true);
    }

    handleBlockSelect(event) {
      if (!(event.target instanceof Element)) return;

      const block = event.target.closest('[data-watch-read-block-type]');
      if (!block || !this.contains(block)) return;

      const tabName = block.dataset.watchReadBlockType === 'youtube_video' ? 'videos' : 'news';
      const tab = this.tabs.find((item) => item.dataset.watchReadTab === tabName);
      if (tab) this.activateTab(tab);
    }

    activateTab(tab, moveFocus = false) {
      const panelId = tab.getAttribute('aria-controls');
      const activePanel = this.panels.find((panel) => panel.id === panelId);
      if (!activePanel) return;

      this.tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', selected.toString());
        item.tabIndex = selected ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel !== activePanel;
        panel.classList.remove('is-entering');
      });

      this.animatePanel(activePanel);

      if (moveFocus) tab.focus();
    }

    animatePanel(panel) {
      if (this.reducedMotion.matches) return;

      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }

      this.animationFrame = requestAnimationFrame(() => {
        panel.classList.add('is-entering');
        panel.addEventListener('animationend', () => panel.classList.remove('is-entering'), { once: true });
      });
    }
  }

  customElements.define('watch-read-tabs', WatchReadTabs);
})();
